import cn from 'bem-cn';

import { useUserContext } from '../../../contexts';
import { useGetAchievementQuery } from '../../../store';
import { Icon, Markdown, Modal, Popover, Stack } from '../../UI';

import { useShareAchievement, useCachedAchievement } from './AchievementModal.hooks';
import { AchievementModalProps } from './AchievementModal.types';
import { AchievementModalSkeleton } from './AchievementModal.skeleton';
import { AchievementModalError } from './AchievementModal.error';
import './AchievementModal.scss';

const b = cn('achievement-modal');

export const AchievementModal = (props: AchievementModalProps) => {
    const { isOpen, onClose, achievementId } = props;

    const { user } = useUserContext();

    const cachedAchievement = useCachedAchievement(achievementId);
    const {
        data: achievement,
        isFetching,
        isError,
    } = useGetAchievementQuery({ id: achievementId || '' }, { skip: !achievementId || !isOpen || !user?.isAuth });

    // Используем данные из кеша пока загружается детальная информация
    const displayAchievement = achievement || cachedAchievement;
    const isOwned = Boolean(achievement?.owned);

    const { shareAchievement, popover, closePopover } = useShareAchievement(displayAchievement, user);

    if (isError && !cachedAchievement) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} showDefaultCloseButton={false}>
                <AchievementModalError onClose={onClose} />
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} showDefaultCloseButton={false}>
            {isFetching && <AchievementModalSkeleton onClose={onClose} />}
            {!isFetching && displayAchievement && (
                <Stack className={b({ owned: isOwned })}>
                    <div className={b('header')}>
                        <Stack className={b('header-button')}>
                            <Popover
                                position="bottom-right"
                                open={popover.open}
                                content={popover.text}
                                autoClose
                                afterClose={closePopover}
                            >
                                <Icon name="share" size={48} onClick={shareAchievement} aria-label="Поделиться" />
                            </Popover>
                        </Stack>
                        <Stack className={b('header-button')}>
                            <Icon name="close" size={48} onClick={onClose} aria-label="Закрыть" />
                        </Stack>
                    </div>
                    <Stack spacing={8} className={b('content')}>
                        <img className={b('image')} src={displayAchievement.imageData.achievementImageUrl} alt="" />
                        <h2>{displayAchievement.title}</h2>
                        <Markdown>{displayAchievement.text}</Markdown>
                        {isOwned && (
                            <Stack direction="row" spacing={8} className={b('owned-message')}>
                                <p>Уже у вас</p>
                                <Icon name="tick" />
                            </Stack>
                        )}
                    </Stack>
                    <div className={b('separator')} />
                    <Stack spacing={8} className={b('footer')}>
                        <p>Условия для выполнения</p>
                        <h3>{displayAchievement.description}</h3>
                    </Stack>
                </Stack>
            )}
        </Modal>
    );
};
